import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, AlertCircle, CheckCircle2, Info, Loader2 } from "lucide-react";
import { analyzeDealWithAI, AIRiskAssessment } from "@/services/aiService";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface AIDealAnalysisProps {
  deal: {
    id?: string;
    title: string;
    description?: string;
    brand_name: string;
    amount: number;
    deliverables?: string;
    deal_type: string;
  };
}

export function AIDealAnalysis({ deal }: AIDealAnalysisProps) {
  const [analysis, setAnalysis] = useState<AIRiskAssessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await analyzeDealWithAI(deal);
      setAnalysis(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadgeColor = (level: string) => {
    switch (level) {
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-muted';
    }
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle>AI Compliance Analysis</CardTitle>
          </div>
          {!analysis && (
            <Button onClick={handleAnalyze} disabled={loading} size="sm">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Get AI Analysis
                </>
              )}
            </Button>
          )}
        </div>
        <CardDescription>
          AI-powered compliance risk assessment using advanced pattern recognition
        </CardDescription>
      </CardHeader>

      {error && (
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      )}

      {analysis && (
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Risk Score</div>
              <div className="text-3xl font-bold">{analysis.riskScore}/100</div>
            </div>
            <Badge className={getRiskBadgeColor(analysis.riskLevel)}>
              {analysis.riskLevel.toUpperCase()} RISK
            </Badge>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold flex items-center gap-2">
              <Info className="h-4 w-4" />
              AI Assessment
            </h4>
            <p className="text-sm text-muted-foreground">{analysis.explanation}</p>
          </div>

          {analysis.concerns.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2 text-orange-600">
                <AlertCircle className="h-4 w-4" />
                Key Concerns
              </h4>
              <ul className="space-y-1">
                {analysis.concerns.map((concern, idx) => (
                  <li key={idx} className="text-sm flex items-start gap-2">
                    <span className="text-orange-500 mt-0.5">•</span>
                    <span>{concern}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.recommendations.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                Recommendations
              </h4>
              <ul className="space-y-1">
                {analysis.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-sm flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">✓</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button onClick={handleAnalyze} variant="outline" size="sm" className="w-full">
            <Sparkles className="mr-2 h-4 w-4" />
            Re-analyze
          </Button>
        </CardContent>
      )}
    </Card>
  );
}
